import queryString from 'query-string';
import type { AffiliateHandoffKind } from './affiliateHandoff.types';

export type AffiliateHandoff = Readonly<{
  kind: AffiliateHandoffKind;
  /**
   * The host the guest actually ends up on — `marriott.com`, not `withjoy.com`.
   *
   * Unwrapped out of the redirect service's `url` parameter. Without this, every direct-booking click in
   * Amplitude reports `destinationDomain: withjoy.com`, because that is genuinely the host of the
   * link we render, and "which advertiser did this click go to" cannot be answered at all.
   *
   * Null when there is no outbound host to name: a relative Joy route, or a URL that will not parse.
   */
  advertiserDomain: string | null;
}>;

const WWW_PREFIX = /^www\./;

const isJoyHost = (hostname: string) => hostname === 'withjoy.com' || hostname.endsWith('.withjoy.com');

const parseAbsolute = (url: string): URL | null => {
  try {
    return new URL(url);
  } catch {
    return null;
  }
};

/**
 * Describe the affiliate handoff encoded in a finished booking URL.
 *
 * Pure and string-in: it reads the URL we are about to hand the browser rather than taking our word
 * for what we built, so a call site that silently stops wrapping is visible in the data instead of
 * continuing to report the intent it no longer honours.
 */
export const describeAffiliateHandoff = (redirectUrl: string | undefined): AffiliateHandoff => {
  if (!redirectUrl) return { kind: 'none', advertiserDomain: null };

  const parsed = parseAbsolute(redirectUrl);

  // A relative path is a Joy route — the room-block and hotel-details interstitials, which build
  // their own outbound link when the guest gets there.
  if (!parsed) return { kind: 'deferred', advertiserDomain: null };

  if (!isJoyHost(parsed.hostname)) {
    return { kind: 'none', advertiserDomain: parsed.hostname.replace(WWW_PREFIX, '') };
  }

  const destination = queryString.parse(parsed.search).url;
  const destinationUrl = Array.isArray(destination) ? destination[0] : destination;

  // A Joy host with no `url` parameter is an in-app route, not a redirect-service handoff.
  if (!destinationUrl) return { kind: 'deferred', advertiserDomain: null };

  const destinationParsed = parseAbsolute(destinationUrl);
  const advertiserDomain = destinationParsed ? destinationParsed.hostname.replace(WWW_PREFIX, '') : null;

  // app-redirect reads the directive off the DESTINATION's own query string, so that is where it
  // has to be read back from too.
  const isHopSkipped = destinationParsed ? queryString.parse(destinationParsed.search).skipRedirectWrapping === 'true' : false;

  return { kind: isHopSkipped ? 'skipped' : 'wrapped', advertiserDomain };
};

/**
 * The same two fields, named as the telemetry payload names them, for spreading into a
 * `hotelBookingRedirect` call.
 *
 * It exists because that payload is rebuilt by hand at five separate call sites — the tile anchor,
 * the CTA button, the new mobile layout, the map marker and the shared listing hook — and adding
 * these fields to one of them is how they came to be missing from the other four. Spreading one
 * helper means a sixth call site either has them or visibly does not.
 */
export const affiliateHandoffTelemetry = (redirectUrl: string | undefined) => {
  const { kind, advertiserDomain } = describeAffiliateHandoff(redirectUrl);
  return { affiliateHandoff: kind, advertiserDomain };
};
